/**
 * CustomerMentionCard Component
 * SESSION_204: Business owner card display for mentions
 * SESSION_209: Added approve/reject buttons for business owners
 * Sprint 037 Session 9: Updated to use customer self-confirmation API with reject dialog
 *
 * A simplified card view for business owners showing:
 * - Confidence score badge (non-expandable)
 * - Review status badge
 * - Emotional tone badge
 * - Comment content with "Show more" expansion
 * - Reddit link
 * - Date information
 * - Approve/Reject buttons (optional, for business owners)
 *
 * Based on MentionReviewCard but without admin actions.
 */
import React, { useState, useCallback } from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  Anchor,
  Box,
  Divider,
  Button
} from '@mantine/core';
import {
  IconBrandReddit,
  IconCalendar,
  IconChevronDown,
  IconChevronUp,
  IconMoodHappy,
  IconMoodSad,
  IconMoodEmpty,
  IconCheck,
  IconX
} from '@tabler/icons-react';
import type { CustomerMentionCardProps } from './CustomerMentionCard.types';
import { parseAsUTC } from '@/infrastructure/utils/dateUtils';
import { appendRedditContext } from '@/utils/redditUtils';
import {
  useConfirmMentionMutation,
  useRejectMentionMutation
} from '@/application/queries/customerMentionQueries';
import { RejectMentionDialog } from '../RejectMentionDialog';

/**
 * Get confidence level info based on score
 * SESSION_205: Normalize score from 0-1 decimal to 0-100 percentage
 */
const getConfidenceLevel = (score: number | null): { color: string; label: string } => {
  if (score === null) {
    return { color: 'gray', label: 'Unknown' };
  }
  // Normalize: if score is <= 1, it's a decimal (0.85 = 85%), otherwise it's already a percentage
  const normalizedScore = score <= 1 ? score * 100 : score;
  if (normalizedScore >= 80) {
    return { color: 'green', label: 'High' };
  }
  if (normalizedScore >= 60) {
    return { color: 'yellow', label: 'Medium' };
  }
  return { color: 'red', label: 'Low' };
};

/**
 * Get review status display info
 */
const getStatusInfo = (matchStatus: string): { color: string; label: string } => {
  switch (matchStatus) {
    case 'Verified':
      return { color: 'green', label: 'Verified' };
    case 'Matched':
      return { color: 'orange', label: 'Pending Review' };
    case 'Dismissed':
      return { color: 'red', label: 'Dismissed' };
    default:
      return { color: 'gray', label: matchStatus || 'Unknown' };
  }
};

/**
 * Get emotional tone icon and color
 */
const getEmotionalToneInfo = (tone: string | null): { icon: React.ReactNode; color: string; label: string } => {
  switch (tone) {
    case 'Positive':
      return { icon: <IconMoodHappy size={14} />, color: 'green', label: 'Positive' };
    case 'Negative':
      return { icon: <IconMoodSad size={14} />, color: 'red', label: 'Negative' };
    case 'Neutral':
    default:
      return { icon: <IconMoodEmpty size={14} />, color: 'gray', label: 'Neutral' };
  }
};

/**
 * Format Reddit creation date with correct UTC interpretation
 */
const formatRedditDate = (dateString: string): string => {
  try {
    const date = parseAsUTC(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  } catch {
    return dateString;
  }
};

/**
 * CustomerMentionCard Component
 */
export const CustomerMentionCard: React.FC<CustomerMentionCardProps> = ({
  mention,
  onClick,
  showApprovalActions = false
}): React.ReactElement => {
  const [showFullContent, setShowFullContent] = useState<boolean>(false);
  // Sprint 037 Session 9: Reject dialog state
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState<boolean>(false);

  // Sprint 037 Session 9: Use new customer self-confirmation mutations
  const confirmMutation = useConfirmMentionMutation();
  const rejectMutation = useRejectMentionMutation();

  const confidenceLevel = getConfidenceLevel(mention.matchConfidenceScore);
  const statusInfo = getStatusInfo(mention.matchStatus);
  const toneInfo = getEmotionalToneInfo(mention.emotionalTone);

  // SESSION_209: Determine if approval actions should be shown
  // Only show if enabled AND mention has a businessId AND mention is not already verified/rejected
  // Note: MatchStatus is 'Unmatched' | 'Matched' | 'Verified' | 'Rejected'
  const canShowApprovalActions = showApprovalActions &&
    mention.businessId !== null &&
    mention.matchStatus !== 'Verified' &&
    mention.matchStatus !== 'Rejected';

  /**
   * Handle approve/confirm button click
   * Sprint 037 Session 9: Updated to use customer self-confirmation API
   */
  const handleConfirm = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation();
    if (mention.businessId) {
      confirmMutation.mutate({
        businessId: mention.businessId,
        mentionId: mention.id
      });
    }
  }, [mention.businessId, mention.id, confirmMutation]);

  /**
   * Handle reject button click - opens reject dialog
   * Sprint 037 Session 9: Now opens dialog for reason selection
   */
  const handleRejectClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsRejectDialogOpen(true);
  }, []);

  /**
   * Handle reject dialog close
   */
  const handleRejectDialogClose = useCallback((): void => {
    setIsRejectDialogOpen(false);
  }, []);

  /**
   * Toggle full content display
   */
  const handleToggleContent = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation();
    setShowFullContent(!showFullContent);
  }, [showFullContent]);

  /**
   * Handle card click
   */
  const handleCardClick = useCallback((): void => {
    if (onClick) {
      onClick(mention);
    }
  }, [mention, onClick]);

  // Check if content is long enough to need expansion
  const isContentLong = mention.content.length > 200 || mention.content.split('\n').length > 3;

  // Build Reddit URL with context for better readability (SESSION_206)
  const rawRedditUrl = mention.postPermalink
    ? mention.postPermalink.startsWith('http')
      ? mention.postPermalink
      : `https://reddit.com${mention.postPermalink}`
    : null;
  const redditUrl = rawRedditUrl ? appendRedditContext(rawRedditUrl) : null;

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      onClick={onClick ? handleCardClick : undefined}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <Stack gap="md">
        {/* Header Row: Confidence | Status | Emotional Tone | Date */}
        <Group justify="space-between" wrap="nowrap">
          {/* Left: Badges */}
          <Group gap="xs">
            <Badge
              color={confidenceLevel.color}
              variant="filled"
              size="lg"
            >
              {Math.round((mention.matchConfidenceScore ?? 0) * 100)}% {confidenceLevel.label}
            </Badge>
            <Badge color={statusInfo.color} variant="light" size="lg">
              {statusInfo.label}
            </Badge>
            <Badge
              color={toneInfo.color}
              variant="outline"
              size="md"
              leftSection={toneInfo.icon}
            >
              {toneInfo.label}
            </Badge>
          </Group>

          {/* Right: Date */}
          <Group gap="xs">
            <IconCalendar size={14} style={{ opacity: 0.6 }} />
            <Text size="xs" c="dimmed">
              {formatRedditDate(mention.postCreatedAt)}
            </Text>
          </Group>
        </Group>

        {/* Post Title */}
        <Box>
          <Text size="sm" fw={600} c="dimmed" mb={4}>
            Post Title:
          </Text>
          <Text size="md" fw={500}>
            {mention.postTitle}
          </Text>
        </Box>

        {/* Comment Content (Expandable) */}
        <Box>
          <Text size="sm" fw={600} c="dimmed" mb={4}>
            Mention Context:
          </Text>
          <Text
            size="sm"
            lineClamp={showFullContent ? undefined : 3}
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {mention.content}
          </Text>
          {isContentLong && (
            <Anchor
              size="sm"
              onClick={handleToggleContent}
              style={{
                cursor: 'pointer',
                marginTop: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {showFullContent ? (
                <>
                  <IconChevronUp size={14} /> Show less
                </>
              ) : (
                <>
                  <IconChevronDown size={14} /> Show more
                </>
              )}
            </Anchor>
          )}
        </Box>

        <Divider />

        {/* Footer: Subreddit + Reddit Link */}
        <Group justify="space-between" wrap="wrap">
          <Group gap="xs">
            <IconBrandReddit size={16} />
            {redditUrl ? (
              <Anchor
                href={redditUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                onClick={(e): void => e.stopPropagation()}
              >
                View on r/{mention.subreddit}
              </Anchor>
            ) : (
              <Text size="sm" c="dimmed">
                r/{mention.subreddit}
              </Text>
            )}
          </Group>

          {/* Author */}
          <Text size="xs" c="dimmed">
            by u/{mention.authorUsername}
          </Text>
        </Group>

        {/* Sprint 037 Session 9: Approval Actions for Business Owners */}
        {canShowApprovalActions && (
          <>
            <Divider />
            <Group justify="flex-end" gap="xs">
              <Button
                size="xs"
                color="green"
                variant="light"
                leftSection={<IconCheck size={14} />}
                loading={confirmMutation.isPending}
                disabled={rejectMutation.isPending || isRejectDialogOpen}
                onClick={handleConfirm}
              >
                Confirm
              </Button>
              <Button
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconX size={14} />}
                disabled={confirmMutation.isPending}
                onClick={handleRejectClick}
              >
                Reject
              </Button>
            </Group>
          </>
        )}
      </Stack>

      {/* Sprint 037 Session 9: Reject Mention Dialog */}
      {mention.businessId && (
        <RejectMentionDialog
          isOpen={isRejectDialogOpen}
          mention={mention}
          businessId={mention.businessId}
          onClose={handleRejectDialogClose}
        />
      )}
    </Card>
  );
};
